import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  OnInit,
}                              from '@angular/core';
import { MAT_DIALOG_DATA }     from '@angular/material/dialog';
import {
  combineLatest,
  filter,
  map,
  take,
}                              from 'rxjs';
import {
  Feature,
  fileIcon,
  needsBrandColor,
  PaymentStatus,
}                              from '~domain/enums';
import {
  isRbDocumentType,
  payDocumentType,
  RbDocument,
  toPaymentData,
  UserSettings,
}                              from '~domain/types';
import {
  DialogButton,
  DialogConfig,
}                              from '~shared/components';
import { FeatureService }      from '../../core';
import { MainService }         from '../../main/main.service';
import { DocumentService }     from '../document.service';
import { ViewDocumentService } from './view-document.service';

export type ViewDocumentRequest = {
  document: RbDocument,
  userSettings: UserSettings,
};

@Component({
  selector: 'rb-view-document-dialog',
  standalone: false,
  templateUrl: './view-document-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewDocumentDialogComponent implements OnInit {
  viewDocumentType$ = this.viewDocumentService.viewDocumentType$;
  viewDocumentUrl$ = this.viewDocumentService.viewDocumentUrl$;
  documentDownloadId$ = this.documentService.documentDownloadingId$;
  canPayDocument$ = this.featureService.officeHasFeature(Feature.ISABEL_PI).pipe(
    map((paymentFeatureEnabled) => paymentFeatureEnabled && !!payDocumentType(this.request.document)),
  );
  fetchingDocumentPaymentDataId$ = this.documentService.fetchingDocumentPaymentDataId$;
  fetchingDocumentAssignmentDataIds$ = this.documentService.fetchingDocumentAssignmentDataIds$;
  toggleDocumentHiddenIsLoadingId$ = this.documentService.toggleDocumentHiddenIsLoadingId$;
  toggleDocumentSharedIsLoadingId$ = this.documentService.toggleDocumentSharedIsLoadingId$;

  dialogConfig: DialogConfig = {
    title: `${this.request.document.name}.${this.request.document.extension}`,
    document: this.request.document,
    closeable: true,
    maximizable: true,
    customWidth: '76rem',
    buttons: [
      {
        id: DialogButton.Cancel,
        label: 'APP.BUTTON.CLOSE.LABEL',
        icon: 'cross',
        style: 'secondary',
      },
    ],
  };

  dialogMaximized: boolean = false;

  protected readonly needsBrandColor = needsBrandColor;
  protected readonly fileIcon = fileIcon;
  protected readonly PaymentStatus = PaymentStatus;
  protected readonly isRbDocumentType = isRbDocumentType;
  protected readonly payDocumentType = payDocumentType;

  constructor(
    private readonly documentService: DocumentService,
    private readonly featureService: FeatureService,
    private readonly mainService: MainService,
    @Inject(MAT_DIALOG_DATA) public request: ViewDocumentRequest,
    private readonly viewDocumentService: ViewDocumentService,
  ) {
  }

  get anyActionLoading$() {
    return combineLatest([
      this.documentDownloadId$,
      this.fetchingDocumentPaymentDataId$,
      this.fetchingDocumentAssignmentDataIds$,
      this.toggleDocumentHiddenIsLoadingId$,
      this.toggleDocumentSharedIsLoadingId$,
    ]).pipe(
      map(([
             documentDownloadId,
             fetchingDocumentPaymentDataId,
             fetchingDocumentAssignmentDataIds,
             toggleDocumentHiddenIsLoadingId,
             toggleDocumentSharedIsLoadingId,
           ]) => (
        documentDownloadId === this.request.document.id
        || fetchingDocumentPaymentDataId === this.request.document.id
        || fetchingDocumentAssignmentDataIds.includes(this.request.document.id)
        || toggleDocumentHiddenIsLoadingId === this.request.document.id
        || toggleDocumentSharedIsLoadingId === this.request.document.id
      )),
    );
  }

  get docViewerMaxHeight() {
    return this.dialogMaximized ? 'calc(100vh - 18.2rem)' : 'min(70.1rem, calc(100vh - 18.2rem))';
  }

  ngOnInit() {
    if (this.request.userSettings.dialogMaximized) {
      this.dialogMaximized = true;
    }
  }

  downloadDocument() {
    const { id, name, extension, client } = this.request.document;
    this.documentService.downloadDocument(name, extension, id, client?.id);
  }

  renameDocument() {
    this.documentService.renameDocument(this.request.document);
  }

  createPayment() {
    this.documentService.openDocumentPaymentDialog(
      this.request.document,
      true,
      this.request.document.client?.id,
    );
  }

  editPayment() {
    if (this.request.document.payment) {
      let paymentData = toPaymentData(this.request.document.payment);
      if (!this.request.document.payment.bankAccount.supportPayments) {
        paymentData = { ...paymentData, accountId: undefined };
      }
      this.documentService.openDocumentPaymentDialog(
        this.request.document,
        true,
        this.request.document.client?.id,
        paymentData,
      );
    }
  }

  assignDocument() {
    this.documentService.assignDocuments([this.request.document]);
  }

  toggleDocumentHidden() {
    const { id, hidden, client } = this.request.document;
    this.documentService.setDocumentHidden(!hidden, id, client?.id);
  }

  toggleDocumentShared() {
    const { id, shared, client } = this.request.document;
    this.documentService.setDocumentShared(!shared, id, client?.id);
  }

  deleteDocument() {
    this.documentService.deleteDocuments([this.request.document]);
  }

  onDialogButtonClick() {
    this.onClose();
  }

  onClose() {
    this.viewDocumentUrl$.pipe(
      take(1),
      filter(Boolean),
    ).subscribe(() => {
      this.viewDocumentService.clearViewDocument();
    });
  }

  onMaximizedChange(newValue: boolean) {
    this.mainService.setUserSettings({ dialogMaximized: newValue });
  }
}
